// Endpoints da Comunicação do módulo Solicitações.
//
// As 3 rotas REAIS da visão externa do profissional (backend
// `aawz_requisicoes_comunicacao`, prefixo /v2/api/requisicoes) já existem e são
// chamadas em runtime via `con.*` no `apiComunicacao`:
//   - conversas()            → GET  /v2/api/requisicoes/conversas/externa
//   - mensagens(id_conversa) → GET  /v2/api/requisicoes/conversas/externa/<id>/mensagens
//   - enviarMensagem()       → POST /v2/api/requisicoes/mensagens
// A identidade externa (escritório/profissional) é derivada da SESSÃO no backend
// (anti-IDOR) — o front NÃO envia identidade; só paginação e o payload de envio.
// Todas respondem HTTP 200 com `status` no body (padrão AAWZ).
//
// Os demais endpoints abaixo NÃO existem na visão externa do profissional: são
// features WhatsApp-internas (conexões/QR/sync, importar histórico, transferir,
// escalar, atribuir, buscar contatos, nova conversa, editar/reagir/remover
// mensagem, respostas rápidas, upload/proxy de mídia, delivery status). O README
// do módulo já levanta que avaliar se fazem sentido em Solicitações depende do
// modelo de dados do backend. Por ora seguem como placeholders (N/A na visão
// externa / a definir) e `apiComunicacao` resolve fixture para eles.
//
// Endpoint isolation (AC11): apenas `apiComunicacao` importa este arquivo.
// Nenhum componente/modal/view conhece endpoint — só `dispatch` → store → API.
// Trocar backend = editar apenas este arquivo + os branches `con.*` na API.
export const comunicacao = {
  // N/A na visão externa: conexões WhatsApp são feature interna (a definir).
  conexoes: () => '/v2/api/solicitacoes/comunicacao/conexoes',
  // RESPONDER (interno): conexões reais do escritório (os times) — reusa o
  // endpoint do atendimento. A visão do atendente precisa delas pra renderizar.
  conexoesAtendimento: () => '/v2/api/atendimento/conexoes',
  // N/A na visão externa: sync de contatos é feature WhatsApp-interna (a definir).
  syncContatos: (id_conexao) =>
    `/v2/api/solicitacoes/comunicacao/conexoes/${id_conexao}/sync-contatos`,
  // N/A na visão externa: sync de chats é feature WhatsApp-interna (a definir).
  syncChats: (id_conexao) =>
    `/v2/api/solicitacoes/comunicacao/conexoes/${id_conexao}/sync-chats`,
  // N/A na visão externa: refresh de chats é feature WhatsApp-interna (a definir).
  refreshChatsList: (id_conexao) =>
    `/v2/api/solicitacoes/comunicacao/conexoes/${id_conexao}/refresh-chats-list`,
  // REAL: trigger de criação de conversa — garante (find-or-create) a conversa de
  // cada solicitação ativa do escritório da sessão (POST explícito). Chamado ao
  // abrir a Comunicação; é o mesmo endpoint que o núcleo deve chamar ao criar
  // uma solicitação.
  sincronizar: () => '/v2/api/requisicoes/conversas/sincronizar',
  // REAL: lista as conversas do solicitante externo da sessão (paginação args).
  conversas: () => '/v2/api/requisicoes/conversas/externa',
  // REAL (RESPONDER/interno): lista as conversas das conexões do profissional
  // interno no tenant interno. Identidade do profissional vem da sessão.
  conversasInterna: () => '/v2/api/requisicoes/conversas/interna',
  // N/A na visão externa: poll incremental ainda não exposto pelo backend (a definir).
  conversasPoll: () => '/v2/api/solicitacoes/comunicacao/conversas/poll',
  // REAL: mensagens de UMA conversa autorizada da sessão (paginação args).
  mensagens: (id_conversa) =>
    `/v2/api/requisicoes/conversas/externa/${id_conversa}/mensagens`,
  // REAL (RESPONDER/interno): mensagens de uma conversa do tenant interno —
  // reusa o endpoint do atendimento (GET lista, POST envia). As mensagens
  // vivem nas tabelas atendimento_* do escritório interno.
  mensagensAtendimento: (id_conversa) =>
    `/v2/api/atendimento/conversas/${id_conversa}/mensagens`,
  // REAL: envio de mensagem de saída (id_conversa/conteudo/id_solicitacao no body).
  enviarMensagem: () => '/v2/api/requisicoes/mensagens',
  // REAL (RESPONDER/interno): resposta do time SEMPRE via plataforma (sem
  // acionar WhatsApp). Não falha por WhatsApp desconectado — nada se perde.
  enviarMensagemInterna: () => '/v2/api/requisicoes/conversas/interna/mensagens',
  // REAL: solicitações (núcleo) vinculadas a UMA conversa — fonte do painel de
  // detalhes (visão GENERATOR). Identidade externa pela sessão; só o id_conversa
  // viaja no path. `id_conversa` encodado (defesa contra `/` `?` no valor).
  solicitacoesDaConversa: (id_conversa) =>
    `/v2/api/requisicoes/conversas/${encodeURIComponent(id_conversa)}/solicitacoes`,
  // REAL (RESPONDER/interno): mesmo painel, mas o solicitante é resolvido pela
  // conversa (não pela sessão) — a nota exibida é a do atendente interno.
  solicitacoesDaConversaInterna: (id_conversa) =>
    `/v2/api/requisicoes/conversas/interna/${encodeURIComponent(id_conversa)}/solicitacoes`,
  // REAL (RESPONDER/interno): conversa 1:1 de UMA solicitação, na visão do
  // atendente interno (detalhe da solicitação no WS — US-6). 404 é resultado
  // VÁLIDO (conversa ainda não existe OU sem posse — o backend não distingue
  // de propósito, para não vazar existência). `id_solicitacao` encodado
  // (defesa contra `/`/`?` no valor).
  conversaPorSolicitacaoInterna: (id_solicitacao) =>
    '/v2/api/requisicoes/conversas/interna/por-solicitacao/' +
    encodeURIComponent(id_solicitacao),
  // REAL (RESPONDER/interno): inicia (find-or-create) a conversa 1:1 de UMA
  // solicitação — trigger explícito do time interno ao clicar em "Responder"
  // no detalhe (US-6). Body { id_solicitacao }. 422 quando o tipo não tem
  // conexão vinculada (mensagem do backend é a que a UI mostra).
  iniciarConversaInterna: () => '/v2/api/requisicoes/conversas/interna/iniciar',
  // REAL: GET da nota interna privada de UMA solicitação (query id_solicitacao).
  // Path base; o caller anexa formatQueryParams({ id_solicitacao }).
  notaSolicitacaoGet: () => '/v2/api/requisicoes/solicitacoes/nota',
  // REAL: PUT da nota interna privada (body { id_solicitacao, nota }).
  notaSolicitacao: () => '/v2/api/requisicoes/solicitacoes/nota',
  // Marca a conversa como lida (zera mensagens_nao_lidas). Identidade pela
  // sessão (anti-IDOR). Split externa×interna por modo (igual conversas/mensagens):
  //  - GENERATOR (cliente/solicitante) → /conversas/<id>/lida
  //  - RESPONDER (operacao-aawz/atendente)   → /conversas/interna/<id>/lida
  marcarLida: (id_conversa) =>
    `/v2/api/requisicoes/conversas/${encodeURIComponent(id_conversa)}/lida`,
  marcarLidaInterna: (id_conversa) =>
    `/v2/api/requisicoes/conversas/interna/${encodeURIComponent(id_conversa)}/lida`,
  // REAL: upload de anexo (multipart: arquivo + tipo + id_conversa) → S3 →
  // { media_url, media_mime_type, media_filename, media_size_bytes, tipo }.
  // Prefixo REAL /v2/api/requisicoes (o gateway do módulo, não o atendimento).
  mediaUpload: () => '/v2/api/requisicoes/media/upload',
  // Visão INTERNA (atendente/RESPONDER): mesmo contrato, escopo por posse de
  // conexão (não por contato). O front roteia por `mode` (activeMode).
  mediaUploadInterna: () => '/v2/api/requisicoes/media/interna/upload',
  // REAL: download base64 fail-closed de UMA mensagem de mídia →
  // { base64, name, extension, mime_type }. Escopo pela sessão (anti-IDOR);
  // fora de escopo ⇒ 404. `id_mensagem` encodado (defesa contra `/`/`?` no valor).
  mediaDownload: (id_mensagem) =>
    `/v2/api/requisicoes/media/${encodeURIComponent(id_mensagem)}`,
  // Download na visão INTERNA (atendente): escopo por posse de conexão.
  mediaDownloadInterna: (id_mensagem) =>
    `/v2/api/requisicoes/media/interna/${encodeURIComponent(id_mensagem)}`,
  // N/A na visão externa: editar/remover mensagem não exposto pelo backend (a definir).
  editarMensagem: (id_mensagem) =>
    `/v2/api/solicitacoes/comunicacao/mensagens/${id_mensagem}`,
  // N/A na visão externa: reagir é feature WhatsApp-interna (a definir).
  reagirMensagem: (id_mensagem) =>
    `/v2/api/solicitacoes/comunicacao/mensagens/${id_mensagem}/reagir`,
  // N/A na visão externa: importar histórico é feature WhatsApp-interna (a definir).
  importarHistorico: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/importar-historico`,
  // N/A na visão externa: nova conversa é orquestrada server-side (a definir).
  novaConversa: () => '/v2/api/solicitacoes/comunicacao/conversas/nova',
  // N/A na visão externa: buscar contatos é feature WhatsApp-interna (a definir).
  buscarContatos: () => '/v2/api/solicitacoes/comunicacao/contatos/buscar',
  // N/A na visão externa: atribuir conversa é fluxo de atendimento interno (a definir).
  atribuirConversa: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/atribuir`,
  // N/A na visão externa: fechar conversa é fluxo de atendimento interno (a definir).
  fecharConversa: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/fechar`,
  // N/A na visão externa: escalar conversa é fluxo de atendimento interno (a definir).
  escalarConversa: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/escalar`,
  // N/A na visão externa: transferir conversa é fluxo de atendimento interno (a definir).
  transferirConversa: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/transferir`,
  // N/A na visão externa: mudar status é fluxo de atendimento interno (a definir).
  mudarStatus: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/status`,
  // N/A na visão externa: respostas rápidas é feature WhatsApp-interna (a definir).
  respostasRapidas: () => '/v2/api/solicitacoes/comunicacao/respostas-rapidas',
  // N/A na visão externa: editar/remover resposta rápida é feature interna (a definir).
  respostaRapida: (id) =>
    `/v2/api/solicitacoes/comunicacao/respostas-rapidas/${id}`,
  // N/A na visão externa: delivery status é feature WhatsApp-interna (a definir).
  deliveryStatus: (id_conversa) =>
    `/v2/api/solicitacoes/comunicacao/conversas/${id_conversa}/delivery-status`,
}

// Endpoints da tela de Configurações do módulo Solicitações (visão INTERNA AAWZ).
// Backend `aawz_requisicoes_comunicacao` (prefixo /v2/api/requisicoes), todas
// HTTP 200 com `status` no body (padrão AAWZ). Apenas `apiConfiguracoes` importa
// estes endpoints (endpoint isolation — componentes não conhecem URL).
export const configuracoes = {
  // GET: catálogo de tipos de solicitação. value: [{ id, nome, id_area }].
  solicitacaoTipos: () => '/v2/api/requisicoes/solicitacao-tipos',
  // GET: vínculos atuais tipo×conexão (value dict único OU lista — normalizar).
  // POST { id_solicitacao_tipo, id_conexao }: cria/re-aponta (idempotente).
  tipoConexao: () => '/v2/api/requisicoes/tipo-conexao',
  // DELETE: remove (soft) o vínculo. `id` é o campo `id` (id_marcacao) do GET.
  tipoConexaoItem: (id) => '/v2/api/requisicoes/tipo-conexao/' + id,
}

// Endpoints REAIS do módulo Solicitações (backend aawz_solicitacoes, prefixo
// /v3/api/aawz_solicitacoes). Catálogo (áreas/tipos) + listagem/detalhe/arquivos
// das solicitações. Apenas `apiSolicitacoes` importa estes endpoints.
export const solicitacoes = {
  areas: '/v3/api/aawz_solicitacoes/aawz-servicos-areas',
  tipos: '/v3/api/aawz_solicitacoes/aawz-servicos-solicitacoes-tipo',
  solicitacoes: '/v3/api/aawz_solicitacoes/aawz-servicos-solicitacoes',
  arquivos: '/v3/api/aawz_solicitacoes/aawz-servicos-solicitacoes-arquivos',
  tarefas: (id) =>
    `/v3/api/aawz_solicitacoes/aawz-servicos-solicitacoes/${encodeURIComponent(
      id
    )}/tarefas`,
  arquivoDownload: (id) =>
    `/v3/api/aawz_solicitacoes/aawz-servicos-solicitacoes-arquivos/${encodeURIComponent(
      id
    )}/download`,
}

// Lead comercial do funil (US4 — spec-006-landing-servico-funil-comercial, D5-D9 em
// DECISIONS.md). Mesmo backend/prefixo do catálogo (aawz_solicitacoes). POST aciona o
// time comercial para um tipo de serviço — idempotente via UNIQUE(id_escritorio,
// id_solicitacao_tipo) no backend: 2º clique (mesmo profissional ou colega) retorna
// 200 com o lead já existente e `ja_acionado: true`, sem novo e-mail/tarefa (D6). GET
// consulta o lead do escritório para UM tipo (query id_solicitacao_tipo), usado pela
// landing pra saber se já foi acionado. HTTP 200 always — status real no body (D14).
// Apenas `apiLeadComercial` importa este endpoint.
export const leadComercial = {
  leadComercial: '/v3/api/aawz_solicitacoes/aawz-servicos-lead-comercial',
}

// Endpoints da ÁREA DE EXECUÇÃO INTERNA (AAWZ profissional) — visão CROSS-OFFICE das
// solicitações que os escritórios estão abrindo (escritório executor 2976). Backend
// aawz_solicitacoes/interno; a guarda do escritório executor + o scoping cross-tenant
// vivem no backend. Apenas `apiSolicitacoesOperacaoAawz` importa estes endpoints.
const OPERACAO_AAWZ_BASE = '/v3/api/aawz_solicitacoes/interno/solicitacoes'
export const solicitacoesOperacaoAawz = {
  solicitacoes: OPERACAO_AAWZ_BASE, // GET lista (cross-tenant) + ?filters={...}
  detalhe: (id) => `${OPERACAO_AAWZ_BASE}/${encodeURIComponent(id)}`, // GET detalhe (1 item)
  status: (id) => `${OPERACAO_AAWZ_BASE}/${encodeURIComponent(id)}/status`, // PUT { status }
  tarefas: (id) => `${OPERACAO_AAWZ_BASE}/${encodeURIComponent(id)}/tarefas`, // GET timeline (pm_task)
  arquivos: (id) => `${OPERACAO_AAWZ_BASE}/${encodeURIComponent(id)}/arquivos`, // GET lista / POST anexar
  // Download de anexo (id do ARQUIVO, não da solicitação) → { base64, name, extension }.
  arquivoDownload: (id) => `${OPERACAO_AAWZ_BASE}/arquivos/${encodeURIComponent(id)}/download`,
}

// Endpoints ADMIN (escritório interno 2976) de parametrização do catálogo
// Backoffice (T017/T018): schema do formulário por serviço, override por
// instituição, responsável-padrão de execução. Backend
// aawz_solicitacoes/resources/interno/resource_interno_admin_parametrizacao.py
// (guard decorator_check_is_escritorio_interno — reforçado no front pela rota
// settings-v2.solicitacoes, requireSolicitacoesInternal). Apenas
// apiCatalogoBackoffice importa estes endpoints.
const CATALOGO_ADMIN_BASE = '/v3/api/aawz_solicitacoes/interno/catalogo'
export const catalogoAdmin = {
  // GET devolve { id, nome, formulario_schema }; PUT espera data
  // { formulario_schema: [...] } (mesmo formato validado em ambos os lados).
  formulario: (idTipo) =>
    `${CATALOGO_ADMIN_BASE}/${encodeURIComponent(idTipo)}/formulario`,
  // GET devolve { id_solicitacao_tipo, instituicao, formulario_schema, modo,
  // ativo } (formulario_schema null = sem override, usa o do tipo). PUT faz
  // upsert (data { formulario_schema }). DELETE zera só o schema — preserva
  // o vínculo (modo/ativo), não remove a instituição da lista.
  formularioInstituicao: (idTipo, instituicao) =>
    `${CATALOGO_ADMIN_BASE}/${encodeURIComponent(idTipo)}/formulario/` +
    encodeURIComponent(instituicao),
  // GET devolve { id_profissional, nome_profissional }. PUT espera data
  // { id_profissional } — grava em TODOS os nodes da árvore de execução.
  responsavel: (idTipo) =>
    `${CATALOGO_ADMIN_BASE}/${encodeURIComponent(idTipo)}/responsavel`,
  // GET de TODOS os vínculos do catálogo numa chamada, para a visão geral
  // serviço x corretora. Devolve [{id_solicitacao_tipo, id_banco, instituicao,
  // status}], inclusive os DESLIGADOS — sem eles a matriz não distingue "foi
  // desligado" de "nunca foi vinculado", que é o que ela existe para mostrar.
  // Não colide com a rota por tipo abaixo (`/instituicoes` vs
  // `/<id>/instituicoes`).
  matrizInstituicoes: `${CATALOGO_ADMIN_BASE}/instituicoes`,
  // GET devolve { id, nome, instituicoes: [{instituicao, status}] } com TODOS
  // os vínculos — inclusive os desligados, que a listagem pública de tipos
  // (`instituicoes_escritorio=true`) filtra fora. Sem este endpoint, um
  // vínculo desligado some da tela de gestão e não teria como ser religado.
  instituicoes: (idTipo) =>
    `${CATALOGO_ADMIN_BASE}/${encodeURIComponent(idTipo)}/instituicoes`,
  // PUT espera data { status } — 'manual' | 'automatico' | 'desligado'. A
  // conversão para as colunas `modo`/`ativo` acontece no backend
  // (`core/vinculo_instituicao_status.py`); o front nunca manda `modo`.
  instituicaoStatus: (idTipo, instituicao) =>
    `${CATALOGO_ADMIN_BASE}/${encodeURIComponent(idTipo)}/instituicoes/` +
    encodeURIComponent(instituicao),
}

const MATURIDADE_BASE = '/v3/api/aawz_solicitacoes'

export const maturidade = {
  estagios: `${MATURIDADE_BASE}/aawz-servicos-estagios-maturidade`,
  contextos: `${MATURIDADE_BASE}/aawz-servicos-contextos`,
  progressoContexto: `${MATURIDADE_BASE}/progresso-contexto`,
  escritorioMaturidade: `${MATURIDADE_BASE}/aawz-escritorio-maturidade`,
  autoindicacao: `${MATURIDADE_BASE}/aawz-servicos-autoindicacao`,
  autoindicacaoItem: (id) =>
    `${MATURIDADE_BASE}/aawz-servicos-autoindicacao/${id}`,
  // Matriz serviço×etapa (obrigatoriedade por estágio). GET lista (leitura
  // compartilhada do catálogo 2976) + PUT upsert por par (id_tipo, nivel_estagio).
  tipoEstagio: `${MATURIDADE_BASE}/aawz-servicos-tipo-estagio`,
  // DELETE de vínculo (limpeza administrativa/API — sem consumidor na UI, que usa
  // toggle binário via PUT obrigatorio=false). Declarado para uso futuro.
  tipoEstagioItem: (id) =>
    `${MATURIDADE_BASE}/aawz-servicos-tipo-estagio/${encodeURIComponent(id)}`,
}

// Inscricao de Web Push do profissional (mesmo backend/prefixo do catalogo).
// Substitui as rotas soltas /push e /api/push_key do application.py, que eram
// abertas — a antiga /push mandava push assinado com a chave VAPID da AAWZ
// para o endereco que o chamador passasse. Apenas `apiPushSubscription`
// importa estes endpoints.
export const pushSubscription = {
  subscription: '/v3/api/aawz_solicitacoes/push-subscription',
  publicKey: '/v3/api/aawz_solicitacoes/push-public-key',
}
